import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PlugZap, RefreshCw } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-CA", { dateStyle: "medium", timeStyle: "short" }) : "—");
const Row = ({ k, v, id }) => (
  <div><dt className="text-xs text-slate-500">{k}</dt><dd className="text-slate-900 break-words" data-testid={`crm-int-${id}`}>{v}</dd></div>
);

function TestButton({ available, onDone }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const run = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await api.post("/admin/crm-integration/test", { confirm: "SEND_SYNTHETIC_TEST" });
      setMsg(r.data.ok ? "Connection test passed." : `Connection test failed (ownership: ${String(r.data.ownership)}, code: ${r.data.event_code ?? "—"}).`);
      onDone();
    } catch (e) { setMsg(e?.response?.data?.detail || "Connection test failed."); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-4">
      <button type="button" disabled={!available || busy} onClick={run} className="pf-btn text-sm disabled:opacity-50" data-testid="crm-int-test-button">
        {busy ? "Testing…" : "Send synthetic connection test"}
      </button>
      {!available && <p className="text-xs text-slate-500 mt-1" data-testid="crm-int-test-unavailable">Available once an approved CRM receiver is configured.</p>}
      {msg && <p className="text-sm mt-2" data-testid="crm-int-test-result">{msg}</p>}
    </div>
  );
}

export const CrmIntegrationPanel = () => {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [err, setErr] = useState(null);
  const load = () => api.get("/admin/crm-integration/status").then((r) => { setS(r.data); setErr(null); }).catch(() => setErr("Could not load CRM integration status."));
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);
  if (err) return <div className="pf-card p-5 mt-6 text-sm text-red-600" data-testid="crm-int-error">{err}</div>;
  if (!s) return null;
  const missing = Object.entries(s.configured).filter(([, v]) => !v).map(([k]) => k);
  return (
    <div className="pf-card p-5 mt-6" data-testid="crm-integration-panel">
      <div className="flex items-center justify-between">
        <h3 className="font-medium text-slate-900 flex items-center gap-2"><PlugZap className="w-4 h-4" /> External CRM integration status</h3>
        <button type="button" onClick={load} className="pf-btn text-xs flex items-center gap-1" data-testid="crm-int-refresh"><RefreshCw className="w-3 h-3" /> Refresh</button>
      </div>
      <p className="text-xs text-slate-500 mt-1">Monitoring only. Secrets and lead details are never shown here.</p>
      <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2 text-sm mt-4">
        <Row id="enabled" k="Sync" v={s.enabled ? "Enabled" : "Disabled"} />
        <Row id="destination" k="Destination check" v={s.destination} />
        <Row id="host" k="Receiver host" v={s.destination_host || "Not set"} />
        <Row id="ownership" k="Ownership verified" v={s.ownership?.ok === undefined ? "Not checked" : `${s.ownership.ok ? "Yes" : "No"} · ${fmt(s.ownership.checked_at)}`} />
        <Row id="last-delivered" k="Last delivery" v={fmt(s.last_delivered_at)} />
        <Row id="pending" k="Pending" v={s.counts.PENDING} />
        <Row id="retrying" k="Retrying" v={`${s.retry.count}${s.retry.next_attempt_at ? ` · next ${fmt(s.retry.next_attempt_at)}` : ""}`} />
        <Row id="failed" k="Failed (final)" v={s.counts.DEAD} />
        <Row id="delivered" k="Delivered" v={s.counts.DELIVERED} />
        <Row id="schema" k="Schema errors" v={`${s.schema_invalid} · ${s.schema}`} />
        <Row id="retention" k="Queue retention" v={`${s.retention_days} days`} />
        <Row id="last-test" k="Last connection test" v={s.last_test ? `${s.last_test.ok ? "Passed" : "Failed"} · ${fmt(s.last_test.at)}` : "Never"} />
      </dl>
      {missing.length > 0 && <p className="text-xs text-slate-600 mt-3" data-testid="crm-int-missing">Not configured: {missing.join(", ")}</p>}
      {s.recent_errors.length > 0 && (
        <ul className="text-xs text-slate-600 mt-3 space-y-1" data-testid="crm-int-errors">
          {s.recent_errors.map((e, i) => (
            <li key={i} className="flex items-center gap-2">
              <span>{e.kind} · {e.status} · attempts {e.attempts} · {e.last_code ?? e.last_error ?? "—"} · {fmt(e.updated_at)}</span>
              {e.status === "DEAD" && user?.role === "super_admin" && (
                <button type="button" className="underline text-cyan-700" data-testid={`crm-int-retry-${i}`}
                  onClick={() => api.post(`/admin/crm-integration/retry/${encodeURIComponent(e.event_id)}`).then(load).catch(() => setErr("Retry failed."))}>Retry</button>
              )}
            </li>
          ))}
        </ul>
      )}
      {user?.role === "super_admin" && <TestButton available={s.test_available} onDone={load} />}
    </div>
  );
};
